export type {
  ProfileMeta,
  ProfileStatus,
  ProfileData,
  ProfileForm,
  PresentatorItem,
} from "@/entities/profile"

export type Profile = {
  id: string
  title: string
}

/** One bullet in topics / client_info (icon = Lucide kebab-case). */
export type CopilotSummaryBullet = {
  text: string
  icon?: string
}

/** Legacy summaries may still store plain strings for topics/client_info. */
export type CopilotSummaryBulletLike = CopilotSummaryBullet | string

export type CopilotAgreedItem = {
  text: string
  done: boolean
}

export type CopilotAgreedItemLike = CopilotAgreedItem | string

export type CopilotBreakdownItem = {
  t_sec: number
  title: string
  detail?: string
}

export type CopilotSummary = {
  session_id?: string
  profile_name?: string
  date?: string
  duration_min?: number
  topics: CopilotSummaryBulletLike[]
  client_info: CopilotSummaryBulletLike[]
  agreed: CopilotAgreedItemLike[]
  follow_up: string[]
  breakdown?: CopilotBreakdownItem[]
}

/** Normalize a summary bullet to text (supports legacy plain strings). */
export function bulletText(item: CopilotSummaryBulletLike): string {
  if (typeof item === "string") return item.trim()
  return (item.text ?? "").trim()
}

/** Lucide kebab-case name for a bullet, or empty for legacy / missing. */
export function bulletIcon(item: CopilotSummaryBulletLike): string {
  if (typeof item === "string") return ""
  return (item.icon ?? "").trim()
}

export function agreedText(item: CopilotAgreedItemLike): string {
  if (typeof item === "string") return item.trim()
  return (item.text ?? "").trim()
}

/** Legacy plain strings count as done. */
export function agreedDone(item: CopilotAgreedItemLike): boolean {
  if (typeof item === "string") return true
  return Boolean(item.done)
}

export function formatTimecode(seconds: number): string {
  const sec = Math.max(0, Math.floor(seconds))
  const mm = Math.floor(sec / 60)
  const ss = sec % 60
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
}

export type LocalAudioRecord = {
  id: string
  sessionId?: string
  file_id?: string
  copilotSessionId?: string
  copilotSummary?: CopilotSummary
  isFinalizing?: boolean
  /** True when summary fetch retries were exhausted for this recording session. */
  summaryFailed?: boolean
  name: string
  url: string
  blob: Blob
  peaks?: number[]
  durationSec?: number
  duration: string
  date: string
  time: string
  createdAt: number
  isUploading?: boolean
  uploadError?: string
}
